import { all, call, put, takeEvery, takeLatest, take, fork } from 'redux-saga/effects';
import isEmpty from 'lodash/isEmpty';
import { EVENT_TYPES } from '../utils/constants';
import {
  OPEN_PUBLISHER_CHANNEL,
  CLOSE_PUBLISHER_CHANNEL,
  FETCH_PUBLISHER_CATEGORIES,
  CREATE_PUBLISHER_CATEGORY,
  CREATE_PUBLISHER_CATEGORY_SUCCESS,
  CREATE_PUBLISHER_CATEGORY_FAIL,
  LOAD_SOUNDCASTS_PUBLISHER_CATEGORIES,
  UPDATE_SOUNDCAST_PUBLISHER_CATEGORY,
  UPDATE_SOUNDCAST_PUBLISHER_CATEGORY_SUCCESS,
  UPDATE_SOUNDCAST_PUBLISHER_CATEGORY_FAIL,
  UPDATE_PUBLISHER_CATEGORIES_ORDER,
  DELETE_PUBLISHER_CATEGORY,
  UPDATE_SOUNDCAST_PUBLISHER_CATEGORY_TITLE,
  UPDATE_SOUNDCAST_PUBLISHER_CATEGORY_TITLE_FAIL,
} from '../actions/types';

import { loadCategories, updatePublisher, loadPublisherCategoryInfo } from '../actions';
import { fetchPublisherPlanUsage } from '../routines';
import {
  getCategories,
  publisherPlanUsage,
  getTagInformation,
  setSoundcastsToTag,
  removeSoundcastsFromTag,
  getSoundcastsList,
  createCategory,
  updateTagSoundcastsOrder,
  deleteCategory,
} from '../managers';
import { createDynamicLiveChannel } from '../helpers/channels';
import {
  clearFeaturedTag,
  setTagAsFeatured,
  updateCategory,
} from '../managers/category';
import { updateTagAsFeatured, clearTagAsFeatured } from '../routines/publisher';

const publisherPlanUsageWorker = function* ({ payload: { token } }) {
  const payload = { token };
  console.log('publisherPlanUsageWorker', payload);
  try {
    yield put(fetchPublisherPlanUsage.request(payload));
    const { data: usage } = yield call(publisherPlanUsage, token);
    yield put(fetchPublisherPlanUsage.success({ ...payload, usage }));
  } catch (error) {
    yield put(fetchPublisherPlanUsage.failure(payload, error));
  } finally {
    yield put(fetchPublisherPlanUsage.fulfill(payload));
  }
};

const publisherPlanUsageWatcher = function* () {
  yield takeLatest(fetchPublisherPlanUsage.TRIGGER, publisherPlanUsageWorker);
};

const publisherHandler = function* (snapshot, publisherId) {
  try {
    const publisher = snapshot.val();
    if (!isEmpty(publisher)) {
      console.log('Publisher from live channel', publisherId, publisher);
      yield put(
        updatePublisher({
          id: publisherId,
          ...publisher,
        })
      );
    }
  } catch (error) {
    console.error(error);
  }
};

const publisherLiveChannelWorker = function* ({ payload: publisherId }) {
  yield call(createDynamicLiveChannel, {
    closeChannelAction: CLOSE_PUBLISHER_CHANNEL,
    eventType: EVENT_TYPES.VALUE,
    handler: publisherHandler,
    pathToListen: `publishers/${publisherId}`,
    entity: publisherId,
    skipFirstRound: false,
  });
};

const soundcastsListWorker = function* ({ publisherId, type }) {
  const soundcastsList = yield call(getSoundcastsList, publisherId, type);
  yield put({
    type: LOAD_SOUNDCASTS_PUBLISHER_CATEGORIES,
    payload: {
      [type]: soundcastsList.data,
    },
  });
  // type = individuals|bundles
};

const updatePublisherSoundcastsCategoryTitleWorker = function* ({ payload }) {
  try {
    yield call(updateCategory, payload);
  } catch (e) {
    yield put({
      type: UPDATE_SOUNDCAST_PUBLISHER_CATEGORY_TITLE_FAIL,
      payload: {
        error: e.message,
      },
    });
  }
}

const updatePublisherSoundcastsCategoryWorker = function* (action) {
  try {
    const { itemsToAdd, itemsToDelete, categoryId, publisherId } = action.payload;
    let newItems = {};
    itemsToAdd.forEach(item => {
      newItems[item] = {};
      newItems[item]['position'] = 1;
    });
    if (!isEmpty(Object.keys(newItems))) {
      yield call(setSoundcastsToTag, {
        tagId: categoryId,
        soundcastsIds: newItems,
      });
    }
    let deleteItems = {};
    itemsToDelete.forEach(item => {
      deleteItems[item] = {};
      deleteItems[item]['position'] = 1;
    });
    if (!isEmpty(Object.keys(deleteItems))) {
      yield call(removeSoundcastsFromTag, {
        tagId: categoryId,
        soundcastsIds: deleteItems,
      });
    }
    yield put({
      type: UPDATE_SOUNDCAST_PUBLISHER_CATEGORY_SUCCESS,
    });

    // Reloading Categories List
    yield put({
      type: FETCH_PUBLISHER_CATEGORIES,
      payload: {
        publisherId,
      },
    });
  } catch (e) {
    yield put({
      type: UPDATE_SOUNDCAST_PUBLISHER_CATEGORY_FAIL,
      payload: {
        error: e.message,
      },
    });
  }
};

const updatePublisherSoundcastsCategoryWatcher = function* () {
  yield takeLatest(UPDATE_SOUNDCAST_PUBLISHER_CATEGORY, updatePublisherSoundcastsCategoryWorker);
  yield takeLatest(UPDATE_SOUNDCAST_PUBLISHER_CATEGORY_TITLE, updatePublisherSoundcastsCategoryTitleWorker);
};

const deletePublisherCategoryWorker = function* ({ payload }) {
  try {
    yield call(deleteCategory, payload);
  } catch (e) {}
  // Reloading Categories List
  yield put({
    type: FETCH_PUBLISHER_CATEGORIES,
    payload: {
      publisherId: payload.publisherId,
    },
  });
};

const deletePublisherCategoryWatcher = function* () {
  yield takeLatest(DELETE_PUBLISHER_CATEGORY, deletePublisherCategoryWorker);
};

const createPublisherCategoriesWorker = function* (action) {
  const { publisherId, title, slug, position } = action.payload;
  try {
    yield call(createCategory, {
      publisherId,
      name: title,
      slug,
      position,
    });
    yield put({
      type: CREATE_PUBLISHER_CATEGORY_SUCCESS,
    });
  } catch (e) {
    yield put({
      type: CREATE_PUBLISHER_CATEGORY_FAIL,
      payload: {
        error: e.message,
      },
    });
  }
};

const createPublisherCategoriesWatcher = function* () {
  yield takeLatest(CREATE_PUBLISHER_CATEGORY, createPublisherCategoriesWorker);
};

const publisherCategoryInfoWorker = function* (payload) {
  try {
    const tagInformation = yield call(getTagInformation, payload);
    // If Category has Information
    if (!isEmpty(tagInformation.data)) {
      yield put(loadPublisherCategoryInfo(tagInformation.data));
    }
  } catch (e) {}
};

const publisherCategoriesOrderWorker = function* ({ payload }) {
  const { categories, publisherId } = payload;
  let positions = {};
  categories.forEach((category, index) => {
    positions[category.id] = {};
    positions[category.id]['position'] = index + 1;
  });
  yield updateTagSoundcastsOrder({
    publisherId,
    positions,
  });
};

const publisherCategoriesOrderWatcher = function* () {
  yield takeLatest(UPDATE_PUBLISHER_CATEGORIES_ORDER, publisherCategoriesOrderWorker);
};

const publisherCategoriesWorker = function* (action) {
  try {
    const categories = yield call(getCategories, action.payload);
    yield fork(soundcastsListWorker, {
      publisherId: action.payload.publisherId,
      type: 'individuals',
    });
    yield fork(soundcastsListWorker, {
      publisherId: action.payload.publisherId,
      type: 'bundles',
    });
    for (var i = 0; i < categories.data.length; i++) {
      yield fork(publisherCategoryInfoWorker, {
        tagId: categories.data[i].id,
        publisherId: action.payload.publisherId,
      });
    }
    yield put(loadCategories(categories.data)); // Loading Categories in Reducer
  } catch (e) {}
};

const updateTagAsFeaturedWorker = function* ({payload}) {
  try {
    yield setTagAsFeatured({
      id: payload.categoryId,
      publisherId: payload.publisherId
    })
    yield put(updateTagAsFeatured.success(payload))
    // Reloading Categories List
    yield put({
      type: FETCH_PUBLISHER_CATEGORIES,
      payload: {
        publisherId: payload.publisherId,
      },
    });
  } catch (error) {
    yield put(updateTagAsFeatured.failure(payload, error))
  }
}

const clearTagAsFeaturedWorker = function* ({payload}) {
  try {
    yield clearFeaturedTag({
      id: payload.categoryId,
      publisherId: payload.publisherId
    })
    yield put(clearTagAsFeatured.success(payload))
    // Reloading Categories List
    yield put({
      type: FETCH_PUBLISHER_CATEGORIES,
      payload: {
        publisherId: payload.publisherId,
      },
    });
  } catch (error) {
    yield put(clearTagAsFeatured.failure(payload, error))
  }
}

const publisherCategoriesWatcher = function* () {
  yield takeLatest(FETCH_PUBLISHER_CATEGORIES, publisherCategoriesWorker);
  yield takeLatest(updateTagAsFeatured.TRIGGER, updateTagAsFeaturedWorker);
  yield takeLatest(clearTagAsFeatured.TRIGGER, clearTagAsFeaturedWorker);
};

const publisherLiveChannelWatcher = function* () {
  yield takeLatest(OPEN_PUBLISHER_CHANNEL, publisherLiveChannelWorker);
};

export default function* () {
  yield all([
    call(publisherLiveChannelWatcher),
    call(publisherPlanUsageWatcher),
    call(publisherCategoriesWatcher),
    call(createPublisherCategoriesWatcher),
    call(updatePublisherSoundcastsCategoryWatcher),
    call(publisherCategoriesOrderWatcher),
    call(deletePublisherCategoryWatcher),
  ]);
}
